// src/components/SensorPage/StrategicData.jsx - Design Ultra Premium
import React, { useState, useEffect, useMemo, memo, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FiThermometer, 
  FiWind, 
  FiTarget, 
  FiHeart, 
  FiZap, 
  FiRefreshCw,
  FiTrendingUp,
  FiAlertCircle,
  FiInfo,
  FiArrowUp,
  FiArrowDown,
  FiActivity,
  FiDroplet,
  FiSun,
  FiCloud,
  FiCheckCircle,
  FiXCircle,
  FiClock,
  FiAward
} from 'react-icons/fi';
import { 
  LineChart, 
  Line, 
  AreaChart, 
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer,
  RadialBarChart,
  RadialBar,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  ComposedChart,
  Scatter
} from 'recharts';
import CountUp from 'react-countup';
import './SensorPageSections.css';

const StrategicData = ({ data, activeSubsection, timeRange, onTimeRangeChange }) => {
  const [animateValues, setAnimateValues] = useState(false);
  const { strategic, current, historical } = data;
  
  // Estado do modal compartilhado no nível do componente principal
  // Isso garante que persista mesmo durante atualizações de dados
  const modalStateRef = useRef(null);
  const modalKeyRef = useRef(0);
  const [modalState, setModalState] = useState(null);

  // Função para formatar tempo baseado no período selecionado
  const formatTimeForChart = (timestamp, range) => {
    const date = new Date(timestamp);
    
    switch (range) {
      case '1h':
        return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
      case '24h':
        return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
      case '7d':
        return date.toLocaleDateString('pt-BR', { weekday: 'short', day: '2-digit', month: '2-digit' });
      case '30d':
        return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
      case '6m':
      case '1y':
      case 'all':
        return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit' });
      default:
        return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    }
  };

  // Funções para calcular PMV/PPD (mesmas do useSensorData.js)
  const calculatePMVFromData = (data) => {
    const CLO = 0.5;
    const MET = 1.2;
    
    if (!data || !data.temperature || data.temperature === null || 
        !data.humidity || data.humidity === null) {
      return null;
    }
    
    const ta = data.temperature;
    const rh = data.humidity;
    const tr = data.temperature || ta;
    const va = 0.1;
    
    const M = MET * 58.15;
    const W = 0;
    const Icl = CLO * 0.155;
    
    const a = 17.27;
    const b = 237.7;
    const alpha = (a * ta) / (b + ta) + Math.log(rh / 100.0);
    const tDew = (b * alpha) / (a - alpha);
    const pa = 611.2 * Math.exp((17.67 * tDew) / (tDew + 243.5));
    
    let fcl;
    if (Icl <= 0.078) {
      fcl = 1.0 + 1.29 * Icl;
    } else {
      fcl = 1.05 + 0.645 * Icl;
    }
    
    let tcl = ta + (35.5 - ta) / (3.5 * (Icl + 0.1));
    let hc;
    
    for (let i = 0; i < 20; i++) {
      const tclDiff = Math.abs(tcl - ta);
      if (tclDiff > 0.1) {
        hc = 2.38 * Math.pow(tclDiff, 0.25);
      } else {
        hc = 12.1 * Math.sqrt(va);
      }
      
      const tclNew = 35.7 - 0.028 * (M - W) - Icl * fcl * (3.96 * Math.pow(10, -8) * fcl * 
                     (Math.pow(tcl + 273, 4) - Math.pow(tr + 273, 4)) + hc * (tcl - ta));
      
      if (Math.abs(tclNew - tcl) < 0.001) {
        tcl = tclNew;
        break;
      }
      tcl = tclNew;
    }
    
    const pmv = (0.303 * Math.exp(-0.036 * M) + 0.028) * 
                ((M - W) - 
                 3.05 * Math.pow(10, -3) * (5733 - 6.99 * (M - W) - pa) - 
                 0.42 * ((M - W) - 58.15) - 
                 1.7 * Math.pow(10, -5) * M * (5867 - pa) - 
                 0.0014 * M * (34 - ta) - 
                 3.96 * Math.pow(10, -8) * fcl * (Math.pow(tcl + 273, 4) - Math.pow(tr + 273, 4)) - 
                 fcl * hc * (tcl - ta));
    
    return parseFloat(Math.max(-3, Math.min(3, pmv)).toFixed(2));
  };

  const calculatePPDFromPMV = (pmv) => {
    if (pmv === null) return null;
    const ppd = 100 - 95 * Math.exp(-0.03353 * Math.pow(pmv, 4) - 0.2179 * Math.pow(pmv, 2));
    return parseFloat(Math.max(5, Math.min(100, ppd)).toFixed(2));
  };

  useEffect(() => {
    setAnimateValues(true);
    const timer = setTimeout(() => setAnimateValues(false), 1000);
    return () => clearTimeout(timer);
  }, [activeSubsection]);

  // Componente wrapper seguro para CountUp
  const SafeCountUp = ({ value, suffix = '', decimals = 2, duration = 2, className = '', start = 0 }) => {
    const [isMounted, setIsMounted] = useState(false);
    const containerRef = useRef(null);
    
    useEffect(() => {
      setIsMounted(true);
    }, []);
    
    // Verificar se value é válido
    const isValidValue = (val) => {
      return val !== null && 
             val !== undefined && 
             typeof val === 'number' &&
             !isNaN(val) && 
             isFinite(val);
    };
    
    if (!isValidValue(value)) {
      return (
        <span className={className || 'metric-value-huge'}>
          N/A{suffix}
        </span>
      );
    }
    
    const safeValue = Math.max(0, value);
    
    if (!isMounted) {
      return (
        <span className={className || 'metric-value-huge'}>
          {safeValue.toFixed(decimals)}{suffix}
        </span>
      );
    }
    
    return (
      <div ref={containerRef}>
        <CountUp
          start={start}
          end={safeValue}
          duration={duration}
          decimals={decimals}
          suffix={suffix}
          className={className || 'metric-value-huge'}
        />
      </div>
    );
  };

  // Componente para Nível de Conforto
  const ComfortView = () => {
    const pmvScale = useMemo(() => [
      { value: -3, label: 'Muito Frio', color: '#3b82f6' },
      { value: -2, label: 'Frio', color: '#60a5fa' },
      { value: -1, label: 'Leve Frio', color: '#93c5fd' },
      { value: 0, label: 'Neutro', color: '#10b981' },
      { value: 1, label: 'Leve Calor', color: '#fbbf24' },
      { value: 2, label: 'Quente', color: '#f97316' },
      { value: 3, label: 'Muito Quente', color: '#ef4444' }
    ], []);

    const pmvPosition = strategic.comfort.pmv != null ? ((strategic.comfort.pmv + 3) / 6) * 100 : 50;

    // Filtrar dados históricos baseado no timeRange selecionado
    // Se não houver dados suficientes para o período solicitado, mostrar todos os dados disponíveis
    const comfortData = useMemo(() => {
      if (!historical || historical.length === 0) {
        return [];
      }

      const now = new Date();
      let filteredData = [];
      let maxPeriodDays = 0;

      // Determinar período máximo solicitado
      switch (timeRange) {
        case '1h':
          maxPeriodDays = 1 / 24; // Menos de 1 dia
          break;
        case '24h':
          maxPeriodDays = 1;
          break;
        case '7d':
          maxPeriodDays = 7;
          break;
        case '30d':
          maxPeriodDays = 30;
          break;
        case '6m':
          maxPeriodDays = 180;
          break;
        case '1y':
          maxPeriodDays = 365;
          break;
        case 'all':
          maxPeriodDays = Infinity; // Sem limite
          break;
        default:
          maxPeriodDays = 1;
      }

      // Filtrar dados baseado no período selecionado
      if (timeRange === 'all') {
        // Mostrar todos os dados disponíveis
        filteredData = historical;
      } else {
        filteredData = historical.filter(item => {
          const itemDate = new Date(item.timestamp);
          const daysDiff = (now - itemDate) / (24 * 60 * 60 * 1000);
          return daysDiff <= maxPeriodDays;
        });
      }

      // Se não houver dados suficientes para o período solicitado, usar todos os dados disponíveis
      if (filteredData.length === 0 && historical.length > 0) {
        console.log(`⚠️ Nenhum dado encontrado para período ${timeRange}, mostrando todos os dados disponíveis`);
        filteredData = historical;
      }

      // Verificar quantos dias de dados realmente temos
      if (filteredData.length > 0) {
        const firstDate = new Date(filteredData[0].timestamp);
        const lastDate = new Date(filteredData[filteredData.length - 1].timestamp);
        const actualDays = (lastDate - firstDate) / (24 * 60 * 60 * 1000);
        console.log(`📊 Dados disponíveis: ${filteredData.length} pontos cobrindo ${actualDays.toFixed(1)} dias`);
      }

      // Mapear dados para o formato do gráfico e calcular PMV/PPD para cada ponto
      return filteredData.map((item) => {
        // Calcular PMV/PPD baseado na temperatura e umidade de cada ponto histórico
        const pmvData = {
          temperature: item.temperature,
          humidity: item.humidity
        };
        const pmv = calculatePMVFromData(pmvData);
        const ppd = pmv !== null ? calculatePPDFromPMV(pmv) : null;
        
        return {
          timestamp: item.timestamp || item.time, // Manter timestamp original para o tooltip
          time: formatTimeForChart(item.timestamp || item.time, timeRange),
          pmv: pmv,
          ppd: ppd,
          temperatura: item.temperature != null ? parseFloat(item.temperature.toFixed(2)) : null,
          umidade: item.humidity != null ? parseFloat(item.humidity.toFixed(2)) : null
        };
      });
    }, [historical, timeRange]);

    return (
      <div className="metric-view-container">
        {/* Header Premium */}
        <div className="metric-header-premium comfort-gradient">
          <div className="header-pattern"></div>
          <div className="header-content">
            <div className="header-left">
              <motion.div 
                className="header-icon-box"
                whileHover={{ rotate: 360 }}
                transition={{ duration: 0.5 }}
              >
                <FiThermometer size={36} />
              </motion.div>
              <div>
                <h1 className="header-title">Nível de Conforto Térmico</h1>
                <p className="header-subtitle">Análise baseada em ISO 7730 (PMV/PPD)</p>
              </div>
            </div>
            <div className="header-right">
              <div className="main-metric">
                <SafeCountUp 
                  value={strategic.comfort.score}
                  suffix="%"
                  duration={2}
                  className="metric-value-huge"
                />
                <div className={`metric-status status-${
                  strategic.comfort.score >= 80 ? 'excellent' : 
                  strategic.comfort.score >= 60 ? 'good' : 'moderate'
                }`}>
                  {strategic.comfort.score >= 80 ? 'EXCELENTE' : 
                   strategic.comfort.score >= 60 ? 'BOM' : 'MODERADO'}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* KPI Cards */}
        <div className="kpi-cards-row">
          <motion.div 
            className="kpi-card-advanced"
            whileHover={{ y: -5 }}
          >
            <div className="kpi-icon-container blue">
              <FiThermometer />
            </div>
            <div className="kpi-content">
              <span className="kpi-label">PMV</span>
              <span className="kpi-value">{strategic.comfort.pmv != null ? parseFloat(strategic.comfort.pmv).toFixed(2) : 'N/A'}</span>
              <div className="kpi-trend down">
                <FiArrowDown /> 0.2%
              </div>
            </div>
            <div className="kpi-sparkline">
              <ResponsiveContainer width="100%" height={40}>
                <LineChart data={comfortData.slice(-10)}>
                  <Line 
                    type="monotone" 
                    dataKey="pmv" 
                    stroke="#3b82f6" 
                    strokeWidth={2}
                    dot={false}
                    connectNulls={true}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </motion.div>

          <motion.div 
            className="kpi-card-advanced"
            whileHover={{ y: -5 }}
          >
            <div className="kpi-icon-container orange">
              <FiAlertCircle />
            </div>
            <div className="kpi-content">
              <span className="kpi-label">PPD</span>
              <span className="kpi-value">{strategic.comfort.ppd != null ? parseFloat(strategic.comfort.ppd).toFixed(2) : 'N/A'}%</span>
              <div className="kpi-trend up">
                <FiArrowUp /> 5%
              </div>
            </div>
            <div className="kpi-progress">
              <div className="progress-bar">
                <div 
                  className="progress-fill orange"
                  style={{ width: `${strategic.comfort.ppd != null ? strategic.comfort.ppd : 0}%` }}
                ></div>
              </div>
            </div>
          </motion.div>

          <motion.div 
            className="kpi-card-advanced"
            whileHover={{ y: -5 }}
          >
            <div className="kpi-icon-container gray">
              <FiInfo />
            </div>
            <div className="kpi-content">
              <span className="kpi-label">CLO</span>
              <span className="kpi-value">{strategic.comfort.clo != null ? parseFloat(strategic.comfort.clo).toFixed(2) : 'N/A'}</span>
              <div className="kpi-status">Vestuário</div>
            </div>
          </motion.div>

          <motion.div 
            className="kpi-card-advanced"
            whileHover={{ y: -5 }}
          >
            <div className="kpi-icon-container gray">
              <FiInfo />
            </div>
            <div className="kpi-content">
              <span className="kpi-label">MET</span>
              <span className="kpi-value">{strategic.comfort.met != null ? parseFloat(strategic.comfort.met).toFixed(2) : 'N/A'}</span>
              <div className="kpi-status">Atividade</div>
            </div>
          </motion.div>
        </div>

        {/* PMV Scale Visualization - MELHORADO */}
        <div className="pmv-scale-container">
          <h3 className="section-title">Escala PMV</h3>
          <div className="pmv-scale-visual">
            <div className="scale-gradient">
              {pmvScale.map((item, index) => (
                <div 
                  key={index} 
                  className="scale-segment"
                  style={{ background: item.color }}
                >
                  <span className="scale-value">{item.value}</span>
                </div>
              ))}
            </div>
            <motion.div 
              className="pmv-indicator"
              style={{ left: `${pmvPosition}%` }}
              animate={{ 
                left: `${pmvPosition}%`,
              }}
              transition={{ 
                type: "spring",
                stiffness: 100,
                damping: 20
              }}
            >
              <div className="indicator-wrapper">
                <div className="indicator-value">
                  PMV: {strategic.comfort.pmv != null ? parseFloat(strategic.comfort.pmv).toFixed(2) : 'N/A'}
                </div>
                <div className="indicator-pin">
                </div>
              </div>
            </motion.div>
            <div className="scale-labels" style={{ 
              display: 'flex', 
              justifyContent: 'space-between',
              marginTop: '85px',
              padding: '0 10px'
            }}>
              <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary, #6b7280)' }}>Muito Frio</span>
              <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary, #6b7280)' }}>Neutro</span>
              <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary, #6b7280)' }}>Muito Quente</span>
            </div>
          </div>
        </div>

        {/* Main Chart */}
        <div className="chart-container-advanced">
          <div className="chart-header">
            <h3 className="chart-title">Análise Temporal</h3>
            <div className="time-selector">
              {[
                { id: '1h', label: '1 Hora' },
                { id: '24h', label: '24 Horas' },
                { id: '7d', label: '7 Dias' },
                { id: '30d', label: '30 Dias' },
                { id: '6m', label: '6 Meses' },
                { id: '1y', label: '1 Ano' },
                { id: 'all', label: 'Tudo' }
              ].map(period => (
                <button
                  key={period.id}
                  className={`time-btn ${timeRange === period.id ? 'active' : ''}`}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (onTimeRangeChange) {
                      onTimeRangeChange(period.id);
                    }
                  }}
                  type="button"
                >
                  {period.label}
                </button>
              ))}
            </div>
          </div>
          <ResponsiveContainer width="100%" height={400}>
            <ComposedChart data={comfortData}>
              <defs>
                <linearGradient id="pmvGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.8}/>
                  <stop offset="95%" stopColor="#3b82f6" stopOpacity={0.1}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="time" stroke="#9ca3af" />
              <YAxis yAxisId="left" stroke="#9ca3af" />
              <YAxis yAxisId="right" orientation="right" stroke="#9ca3af" />
              <Tooltip 
                shared={true}
                content={({ active, payload, label }) => {
                  if (!active || !payload || payload.length === 0) {
                    return null;
                  }
                  
                  // Filtrar apenas valores válidos
                  const validEntries = payload.filter(entry => 
                    entry.value !== null && entry.value !== undefined && !isNaN(entry.value)
                  );
                  
                  // Se não houver valores válidos, não mostrar tooltip
                  if (validEntries.length === 0) {
                    return null;
                  }
                  
                  // Determinar se deve mostrar horário baseado no período selecionado
                  const showTime = timeRange === '1h' || timeRange === '24h';
                  
                  // Obter timestamp do payload (usar o primeiro payload válido)
                  let dateTimeStr = label;
                  const firstValidEntry = validEntries[0];
                  if (firstValidEntry && firstValidEntry.payload) {
                    const dataPoint = firstValidEntry.payload;
                    if (dataPoint.timestamp) {
                      const date = new Date(dataPoint.timestamp);
                      if (showTime) {
                        // Até 24 horas: mostrar data e horário completo
                        dateTimeStr = date.toLocaleString('pt-BR', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                          second: '2-digit'
                        });
                      } else {
                        // Acima de 24 horas: mostrar apenas data
                        dateTimeStr = date.toLocaleDateString('pt-BR', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric'
                        });
                      }
                    }
                  }
                  
                  return (
                    <div style={{
                      background: 'rgba(255, 255, 255, 0.95)',
                      border: 'none',
                      borderRadius: '12px',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                      padding: '12px',
                      minWidth: '180px'
                    }}>
                      {/* Data e Horário */}
                      <div style={{
                        fontSize: '12px',
                        color: '#6b7280',
                        fontWeight: '600',
                        marginBottom: '8px',
                        paddingBottom: '8px',
                        borderBottom: '1px solid #e5e7eb'
                      }}>
                        {dateTimeStr}
                      </div>
                      
                      {/* Valores - apenas os válidos */}
                      {validEntries.map((entry, index) => {
                        const value = entry.value;
                        const name = entry.name;
                        const color = entry.color || '#3b82f6';
                        
                        return (
                          <div key={index} style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginBottom: index < validEntries.length - 1 ? '6px' : '0',
                            fontSize: '14px'
                          }}>
                            <span style={{ 
                              color: '#4b5563', 
                              fontWeight: '500',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px'
                            }}>
                              <span style={{
                                width: '8px',
                                height: '8px',
                                borderRadius: '50%',
                                background: color,
                                display: 'inline-block'
                              }}></span>
                              {name}:
                            </span>
                            <span style={{ 
                              color: color, 
                              fontWeight: '700',
                              marginLeft: '12px',
                              fontSize: '15px'
                            }}>
                              {parseFloat(value).toFixed(2)}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  );
                }}
              />
              <Legend />
              <Area
                yAxisId="left"
                type="monotone"
                dataKey="pmv"
                stroke="#3b82f6"
                fill="url(#pmvGradient)"
                name="PMV"
                connectNulls={true}
                dot={(props) => {
                  // Mostrar ponto apenas se o valor for válido
                  const { payload, cx, cy } = props;
                  if (payload && payload.pmv !== null && payload.pmv !== undefined && !isNaN(payload.pmv)) {
                    return (
                      <svg x={cx - 3} y={cy - 3} width={6} height={6}>
                        <circle cx={3} cy={3} r={3} fill="#3b82f6" stroke="#fff" strokeWidth={1} />
                      </svg>
                    );
                  }
                  return null;
                }}
                activeDot={{ r: 6, stroke: '#3b82f6', strokeWidth: 2, fill: '#fff' }}
              />
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="ppd"
                stroke="#f97316"
                strokeWidth={2}
                name="PPD (%)"
                connectNulls={true}
                dot={(props) => {
                  // Mostrar ponto apenas se o valor for válido
                  const { payload, cx, cy } = props;
                  if (payload && payload.ppd !== null && payload.ppd !== undefined && !isNaN(payload.ppd)) {
                    return (
                      <svg x={cx - 3} y={cy - 3} width={6} height={6}>
                        <circle cx={3} cy={3} r={3} fill="#f97316" stroke="#fff" strokeWidth={1} />
                      </svg>
                    );
                  }
                  return null;
                }}
                activeDot={{ r: 6, stroke: '#f97316', strokeWidth: 2, fill: '#fff' }}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>
    );
  };

  // Componente para Qualidade do Ar
  const AirQualityView = () => {
    // Funções para determinar nível e interpretação
    // Função auxiliar para verificar se um valor é válido
    const hasValidValue = (value) => {
      return value != null && !isNaN(value) && isFinite(value);
    };

    const getCO2Level = (value) => {
      if (!hasValidValue(value)) {
        return { level: 'Sem dados', interpretation: 'Sensor não possui leitura de CO₂', color: '#9ca3af' };
      }
      const numValue = Number(value);
      if (numValue < 700) return { level: 'Excelente', interpretation: 'Excelente ventilação, alta renovação de ar', color: '#10b981' };
      if (numValue < 1000) return { level: 'Aceitável', interpretation: 'Ventilação adequada, dentro da ASHRAE', color: '#3b82f6' };
      if (numValue < 1500) return { level: 'Atenção', interpretation: 'Ventilação insuficiente; queda de atenção e produtividade', color: '#f59e0b' };
      if (numValue < 2000) return { level: 'Crítico', interpretation: 'Risco aumentado de dores de cabeça, sonolência, baixa oxigenação', color: '#ef4444' };
      return { level: 'Muito Crítico', interpretation: 'Deve ventilar urgentemente', color: '#dc2626' };
    };

    const getVOCLevel = (value) => {
      if (!hasValidValue(value)) {
        return { level: 'Sem dados', interpretation: 'Sensor não possui leitura de VOC', color: '#9ca3af' };
      }
      const numValue = Number(value);
      if (numValue < 200) return { level: 'Excelente', interpretation: 'Ar muito limpo', color: '#10b981' };
      if (numValue < 400) return { level: 'Bom', interpretation: 'Dentro do aceitável', color: '#3b82f6' };
      if (numValue < 1000) return { level: 'Atenção', interpretation: 'Possível fonte de emissão (móveis, tintas, produtos químicos)', color: '#f59e0b' };
      return { level: 'Crítico', interpretation: 'Deve identificar e mitigar fonte; risco de irritações', color: '#ef4444' };
    };

    const getPM25Level = (value) => {
      if (!hasValidValue(value)) {
        return { level: 'Sem dados', interpretation: 'Sensor não possui leitura de PM2.5', color: '#9ca3af' };
      }
      const numValue = Number(value);
      if (numValue < 5) return { level: 'Excelente', interpretation: 'Dentro do padrão OMS', color: '#10b981' };
      if (numValue < 12) return { level: 'Bom', interpretation: 'Boa qualidade', color: '#3b82f6' };
      if (numValue < 25) return { level: 'Moderado', interpretation: 'Risco leve a moderado', color: '#f59e0b' };
      if (numValue < 35) return { level: 'Ruim', interpretation: 'Nível inadequado', color: '#ef4444' };
      return { level: 'Muito Ruim', interpretation: 'Risco imediato para pessoas sensíveis', color: '#dc2626' };
    };

    // Filtrar apenas parâmetros que têm dados válidos
    const airData = useMemo(() => {
      const hasValidValue = (value) => value != null && !isNaN(value) && isFinite(value);
      
      const data = [];
      if (hasValidValue(strategic.airQuality.co2)) {
        data.push({ name: 'CO₂', value: strategic.airQuality.co2, max: 1000, unit: 'ppm', color: '#10b981', hasData: true });
      } else {
        data.push({ name: 'CO₂', value: null, max: 1000, unit: 'ppm', color: '#9ca3af', hasData: false });
      }
      
      if (hasValidValue(strategic.airQuality.voc)) {
        data.push({ name: 'VOC', value: strategic.airQuality.voc, max: 500, unit: 'ppb', color: '#f59e0b', hasData: true });
      } else {
        data.push({ name: 'VOC', value: null, max: 500, unit: 'ppb', color: '#9ca3af', hasData: false });
      }
      
      if (hasValidValue(strategic.airQuality.pm25)) {
        data.push({ name: 'PM2.5', value: strategic.airQuality.pm25, max: 35, unit: 'µg/m³', color: '#ef4444', hasData: true });
      } else {
        data.push({ name: 'PM2.5', value: null, max: 35, unit: 'µg/m³', color: '#9ca3af', hasData: false });
      }
      
      return data;
    }, [strategic.airQuality]);

    // Dados de referência com interpretações - manter todos para mostrar limites mesmo sem dados
    const referenceData = useMemo(() => {
      const hasValidValue = (value) => value != null && !isNaN(value) && isFinite(value);
      
      return [
        {
          name: 'CO₂',
          value: strategic.airQuality.co2,
          unit: 'ppm',
          level: getCO2Level(strategic.airQuality.co2),
          hasData: hasValidValue(strategic.airQuality.co2),
          limits: [
            { range: '< 700 ppm', level: 'Excelente', color: '#10b981' },
            { range: '700–1000 ppm', level: 'Aceitável', color: '#3b82f6' },
            { range: '1000–1500 ppm', level: 'Atenção', color: '#f59e0b' },
            { range: '> 1500 ppm', level: 'Crítico', color: '#ef4444' },
            { range: '> 2000 ppm', level: 'Muito Crítico', color: '#dc2626' }
          ]
        },
        {
          name: 'VOC',
          value: strategic.airQuality.voc,
          unit: 'ppb',
          level: getVOCLevel(strategic.airQuality.voc),
          hasData: hasValidValue(strategic.airQuality.voc),
          limits: [
            { range: '0–200 ppb', level: 'Excelente', color: '#10b981' },
            { range: '200–400 ppb', level: 'Bom', color: '#3b82f6' },
            { range: '400–1000 ppb', level: 'Atenção', color: '#f59e0b' },
            { range: '> 1000 ppb', level: 'Crítico', color: '#ef4444' }
          ]
        },
        {
          name: 'PM2.5',
          value: strategic.airQuality.pm25,
          unit: 'µg/m³',
          level: getPM25Level(strategic.airQuality.pm25),
          hasData: hasValidValue(strategic.airQuality.pm25),
          limits: [
            { range: '0–5 µg/m³', level: 'Excelente', color: '#10b981' },
            { range: '5–12 µg/m³', level: 'Bom', color: '#3b82f6' },
            { range: '12–25 µg/m³', level: 'Moderado', color: '#f59e0b' },
            { range: '> 25 µg/m³', level: 'Ruim', color: '#ef4444' },
            { range: '> 35 µg/m³', level: 'Muito Ruim', color: '#dc2626' }
          ]
        }
      ];
    }, [strategic.airQuality]);

    const radarData = useMemo(() => [
      { metric: 'CO₂', value: strategic.airQuality.co2 != null ? parseFloat(((600 - strategic.airQuality.co2) / 6).toFixed(2)) : 0, fullMark: 100 },
      { metric: 'VOC', value: strategic.airQuality.voc != null ? parseFloat(((250 - strategic.airQuality.voc) / 2.5).toFixed(2)) : 0, fullMark: 100 },
      { metric: 'PM2.5', value: strategic.airQuality.pm25 != null ? parseFloat(((15 - strategic.airQuality.pm25) / 0.15).toFixed(2)) : 0, fullMark: 100 },
      { metric: 'Umidade', value: current.humidity != null ? parseFloat(current.humidity.toFixed(2)) : 0, fullMark: 100 },
      { metric: 'Temperatura', value: current.temperature != null ? parseFloat((((25 - Math.abs(current.temperature - 22)) / 25) * 100).toFixed(2)) : 0, fullMark: 100 }
    ], [strategic.airQuality, current]);

    return (
      <div className="metric-view-container">
        {/* Header Premium */}
        <div className="metric-header-premium air-gradient">
          <div className="header-pattern"></div>
          <div className="header-content">
            <div className="header-left">
              <motion.div 
                className="header-icon-box"
                animate={{ rotate: [0, 360] }}
                transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
              >
                <FiWind size={36} />
              </motion.div>
              <div>
                <h1 className="header-title">Qualidade do Ar Interior</h1>
                <p className="header-subtitle">Monitoramento de CO₂, VOC e PM2.5</p>
              </div>
            </div>
            <div className="header-right">
              <div className="main-metric">
                {strategic.airQuality.index != null ? (
                  <>
                    <SafeCountUp 
                      value={strategic.airQuality.index}
                      suffix="/100"
                      duration={2}
                      className="metric-value-huge"
                    />
                    <div className={`metric-status status-${
                      strategic.airQuality.index >= 80 ? 'excellent' : 
                      strategic.airQuality.index >= 60 ? 'good' : 'moderate'
                    }`}>
                      {strategic.airQuality.classification.toUpperCase()}
                    </div>
                  </>
                ) : (
                  <>
                    <div className="metric-value-huge">N/A</div>
                    <div className="metric-status status-poor">
                      DADOS INSUFICIENTES
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Pollutant Cards */}
        <div className="pollutant-grid">
          {airData.map((pollutant, index) => (
            <motion.div 
              key={pollutant.name}
              className="pollutant-card"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.1 }}
              whileHover={{ scale: 1.02 }}
            >
              <div className="pollutant-header">
                <span className="pollutant-name">{pollutant.name}</span>
                <span className="pollutant-value">
                  {pollutant.hasData 
                    ? `${parseFloat(pollutant.value).toFixed(2)} ${pollutant.unit}`
                    : 'N/D'}
                </span>
              </div>
              {pollutant.hasData ? (
                <>
                  <div className="pollutant-gauge">
                    <div className="gauge-background"></div>
                    <motion.div 
                      className="gauge-fill"
                      style={{ 
                        width: `${Math.min((pollutant.value / pollutant.max) * 100, 100)}%`,
                        background: pollutant.color
                      }}
                      initial={{ width: 0 }}
                      animate={{ width: `${Math.min((pollutant.value / pollutant.max) * 100, 100)}%` }}
                      transition={{ duration: 1, delay: index * 0.2 }}
                    />
                    <div className="gauge-markers">
                      <span>0</span>
                      <span>{pollutant.max}</span>
                    </div>
                  </div>
                  <div className={`pollutant-status ${
                    (pollutant.value / pollutant.max) < 0.5 ? 'good' : 
                    (pollutant.value / pollutant.max) < 0.8 ? 'moderate' : 'poor'
                  }`}>
                    {(pollutant.value / pollutant.max) < 0.5 ? 'Excelente' : 
                     (pollutant.value / pollutant.max) < 0.8 ? 'Moderado' : 'Atenção'}
                  </div>
                </>
              ) : (
                <>
                  <div className="pollutant-gauge" style={{ opacity: 0.3 }}>
                    <div className="gauge-background"></div>
                    <div className="gauge-markers">
                      <span>0</span>
                      <span>{pollutant.max}</span>
                    </div>
                  </div>
                  <div className="pollutant-status no-data">
                    <FiAlertCircle size={16} />
                    <span>Dados não disponíveis</span>
                  </div>
                  <div className="pollutant-warning">
                    <FiInfo size={14} />
                    <span>Sensor não possui leitura de {pollutant.name}</span>
                  </div>
                </>
              )}
            </motion.div>
          ))}
        </div>

        {/* Referência e Interpretação */}
        <div className="reference-section">
          <h3 className="section-title">Limites de Referência e Interpretação</h3>
          <div className="reference-grid">
            {referenceData.map((ref, index) => (
              <motion.div
                key={ref.name}
                className="reference-card"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -5 }}
              >
                <div className="reference-header">
                  <h4 className="reference-name">{ref.name}</h4>
                  <div className="reference-current">
                    <span className="current-value">
                      {ref.hasData 
                        ? `${parseFloat(ref.value).toFixed(2)} ${ref.unit}`
                        : 'N/D'}
                    </span>
                    <div 
                      className="current-level-badge"
                      style={{ background: ref.level.color, opacity: ref.hasData ? 1 : 0.6 }}
                    >
                      {ref.level.level}
                    </div>
                  </div>
                </div>
                {ref.hasData ? (
                  <div className="reference-interpretation">
                    <FiInfo className="info-icon-small" />
                    <p>{ref.level.interpretation}</p>
                  </div>
                ) : (
                  <div className="reference-interpretation no-data">
                    <FiAlertCircle className="info-icon-small" />
                    <p><strong>Dados não disponíveis:</strong> {ref.level.interpretation}</p>
                  </div>
                )}
                <div className="reference-limits">
                  <div className="limits-header">Limites de Referência:</div>
                  <div className="limits-table">
                    <div className="limits-row header-row">
                      <span>Nível</span>
                      <span>Concentração</span>
                    </div>
                    {ref.limits.map((limit, idx) => (
                      <div 
                        key={idx}
                        className={`limits-row ${ref.level.level === limit.level ? 'active-limit' : ''}`}
                      >
                        <span className="limit-level">
                          <span 
                            className="limit-indicator"
                            style={{ background: limit.color }}
                          ></span>
                          {limit.level}
                        </span>
                        <span className="limit-range">{limit.range}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Radar Chart - MELHORADO */}
        <div className="chart-container-advanced radar-chart-container">
          <h3 className="chart-title">Análise Multidimensional da Qualidade do Ar</h3>
          <ResponsiveContainer width="100%" height={400}>
            <RadarChart data={radarData}>
              <PolarGrid 
                stroke="#e5e7eb" 
                strokeWidth={1}
                radialLines={true}
              />
              <PolarAngleAxis 
                dataKey="metric" 
                tick={{ 
                  fill: '#4b5563', 
                  fontSize: 12,
                  fontWeight: 500
                }}
                className="radar-axis-text"
              />
              <PolarRadiusAxis 
                angle={90} 
                domain={[0, 100]} 
                tick={{ 
                  fill: '#9ca3af', 
                  fontSize: 10 
                }}
                axisLine={false}
                tickCount={5}
              />
              <Radar 
                name="Qualidade" 
                dataKey="value" 
                stroke="#3b82f6" 
                fill="#3b82f6" 
                fillOpacity={0.5}
                strokeWidth={2}
              />
              <Tooltip 
                contentStyle={{
                  background: 'rgba(255, 255, 255, 0.95)',
                  border: 'none',
                  borderRadius: '12px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                  padding: '12px',
                  fontSize: '14px'
                }}
                formatter={(value) => [`${parseFloat(value).toFixed(2)}%`, 'Valor']}
              />
            </RadarChart>
          </ResponsiveContainer>
        </div>
      </div>
    );
  };

  // Componente de Referências Científicas
  const ScientificReferenceTooltip = ({ factor, isOpen, onClose }) => {
    const references = {
      concentration: {
        title: "Índice de Concentração (IC)",
        description: "O Índice de Concentração mede a capacidade de foco e atenção baseado nas condições ambientais.",
        formula: "IC = 100 × fCO₂ × fPM2.5 × fTemp",
        formulaDetails: "Onde cada fator varia entre 0 e 1 e representa a penalidade causada pelo ambiente.",
        references: [
          {
            author: "Cedeño-Laurent et al., 2021",
            title: "Environmental Research Letters - Global COGfx",
            details: "Estudo longitudinal com 302 trabalhadores em 6 países mostrando efeitos do PM2.5 na atenção",
            page: "Tabela 3 p. 8-9",
            pdf: "Cedeño_Laurent_2021_Environ._Res._Lett._16_094047.pdf"
          },
          {
            author: "Satish et al., 2012",
            title: "Is CO₂ an Indoor Pollutant?",
            details: "Efeitos significativos do CO₂ em funções cognitivas já em 1000 ppm",
            page: "p. 4-5",
            pdf: "qt222631x2.pdf"
          },
          {
            author: "Seppänen & Fisk",
            title: "LBNL-60946",
            details: "Melhor desempenho entre 21-22°C. Queda de 1-2% por °C acima de 23°C",
            page: "p. 2-4",
            pdf: "lbnl-60946.pdf"
          }
        ]
      },
      productivity: {
        title: "Índice de Produtividade (IP)",
        description: "O Índice de Produtividade mede a eficiência no trabalho e propensão a erros, com maior peso para CO₂ e PM2.5 que afetam diretamente a pressão de decisão.",
        formula: "IP = 100 × fCO₂^0.7 × fPM2.5^0.8 × fTemp^0.5",
        formulaDetails: "Os expoentes refletem o peso maior de CO₂ e PM2.5 na produtividade, baseado em estudos de xadrez e SMS que mostram efeitos diretos na tomada de decisão.",
        references: [
          {
            author: "Künn et al., 2020",
            title: "IZA Discussion Paper 12632 - Xadrez",
            details: "+10 μg/m³ de PM2.5 → +2,1 p.p. erros; até +3,2 p.p. sob pressão. +300 ppm CO₂ → +1,8 p.p. erros",
            page: "p. 20",
            pdf: "dp12632.pdf"
          },
          {
            author: "Allen et al., 2016",
            title: "Cognitive Function & IAQ - Green vs Conventional office",
            details: "Pontuações cognitivas 61% maiores em ambiente Green e 101% maiores em Green+ comparado ao Convencional",
            page: "p. 2-4 e Tabela p. 5-7",
            pdf: "allen-et-al-2015-associations-o.pdf"
          },
          {
            author: "Cedeño-Laurent et al., 2021",
            title: "Environmental Research Letters - Global COGfx",
            details: "Para cada 10 μg/m³ de PM2.5 → 0,8 a 0,9% mais lento e queda de 0,8-1,7% no throughput",
            page: "Tabela 3 p. 8-9",
            pdf: "Cedeño_Laurent_2021_Environ._Res._Lett._16_094047.pdf"
          }
        ]
      },
      co2: {
        title: "Fator CO₂ - Efeitos Cognitivos Diretos",
        description: "Fórmula baseada em estudos que demonstram efeitos significativos do CO₂ na função cognitiva.",
        formula: "fCO₂ varia de 1.0 (ideal) a 0.1 (muito crítico) conforme a concentração",
        references: [
          {
        author: "Satish et al., 2012",
        title: "Is CO₂ an Indoor Pollutant?",
        details: "Teste SMS em 600 / 1000 / 2500 ppm",
        page: "p. 4-5",
        pdf: "qt222631x2.pdf"
      },
      {
        author: "Allen et al., 2016",
        title: "Cognitive Function & IAQ - Green vs Conventional office",
        details: "Design do estudo Green vs Conventional",
        page: "p. 2-4 e Tabela p. 5-7",
        pdf: "allen-et-al-2015-associations-o.pdf"
      },
      {
        author: "Cao et al., 2022",
        title: "Atmosphere",
        details: "Conclusões: efeitos fortes acima de 3500-5000 ppm",
        page: "p. 14-15",
        pdf: "atmosphere-13-00891-v2.pdf"
      }
        ]
      },
      pm25: {
        title: "Fator PM2.5 - Atenção, Velocidade e Erros",
        description: "Fórmula baseada em estudos que demonstram efeitos do PM2.5 na atenção e propensão a erros.",
        formula: "fPM2.5 varia de 1.0 (ideal) a 0.2 (muito ruim) conforme a concentração",
        references: [
          {
        author: "Cedeño-Laurent et al., 2021",
        title: "Environmental Research Letters - Global COGfx",
        details: "Para cada 10 μg/m³ → 0,8 a 0,9% mais lento (resposta)",
        page: "Tabela 3 p. 8-9",
        pdf: "Cedeño_Laurent_2021_Environ._Res._Lett._16_094047.pdf"
      },
      {
        author: "Künn et al., 2020",
        title: "IZA Discussion Paper 12632 - Xadrez",
        details: "+10 μg/m³ → +2,1 p.p. erros; até +3,2 p.p. sob pressão",
        page: "p. 20",
        pdf: "dp12632.pdf"
      }
        ]
      },
      temp: {
        title: "Fator Temperatura - Efeito no Desempenho",
        description: "Fórmula baseada em revisão de estudos sobre temperatura e desempenho em escritório.",
        formula: "fTemp varia conforme desvio da temperatura ideal de 21,5°C",
        references: [
          {
        author: "Seppänen & Fisk",
        title: "LBNL-60946",
        details: "Melhor desempenho entre 21-22°C. Queda de 1-2% por °C acima de 23°C",
        page: "p. 2-4",
        pdf: "lbnl-60946.pdf"
      }
        ]
      }
    };

    const refData = references[factor];
    if (!refData || !isOpen) return null;

    if (!isOpen || !refData) return null;

    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="reference-overlay"
        onClick={onClose}
      >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="reference-modal"
              onClick={(e) => e.stopPropagation()}
            >
            <div className="reference-modal-header">
              <h3>{refData.title}</h3>
              <button className="close-btn" onClick={onClose}>
                <FiXCircle size={24} />
              </button>
            </div>
            <div className="reference-modal-content">
              <p className="reference-description">{refData.description}</p>
              <div className="reference-formula">
                <strong>Fórmula:</strong>
                <code>{refData.formula}</code>
                {refData.formulaDetails && (
                  <p className="formula-details">{refData.formulaDetails}</p>
                )}
              </div>
              <div className="reference-list">
                <h4>Referências Científicas:</h4>
                {refData.references.map((ref, idx) => (
                  <div key={idx} className="reference-item">
                    <div className="reference-author">{ref.author}</div>
                    <div className="reference-title">{ref.title}</div>
                    <div className="reference-details">{ref.details}</div>
                    <div className="reference-citation">
                      <span className="reference-page">Página: {ref.page}</span>
                      <span className="reference-pdf">PDF: {ref.pdf}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        </motion.div>
    );
  };

  // Funções auxiliares para calcular fatores (fallback se não vierem do backend)
  const calculateFCO2Local = (co2) => {
    if (co2 == null || isNaN(co2)) return null;
    const co2Value = Number(co2);
    if (co2Value <= 800) return 1;
    if (co2Value <= 1000) return 1 - 0.05 * ((co2Value - 800) / 200);
    if (co2Value <= 1500) return 0.95 - 0.10 * ((co2Value - 1000) / 500);
    if (co2Value <= 2500) return 0.85 - 0.25 * ((co2Value - 1500) / 1000);
    if (co2Value <= 5000) return 0.60 - 0.50 * ((co2Value - 2500) / 2500);
    return 0.10;
  };

  const calculateFPM25Local = (pm25) => {
    if (pm25 == null || isNaN(pm25)) return null;
    const pmValue = Number(pm25);
    if (pmValue <= 10) return 1;
    if (pmValue <= 25) return 1 - 0.015 * ((pmValue - 10) / 15);
    if (pmValue <= 35) return 0.985 - 0.04 * ((pmValue - 25) / 10);
    if (pmValue <= 55) return 0.945 - 0.20 * ((pmValue - 35) / 20);
    if (pmValue <= 100) return 0.745 - 0.50 * ((pmValue - 55) / 45);
    return 0.20;
  };

  const calculateFTempLocal = (temp) => {
    if (temp == null || isNaN(temp)) return null;
    const tempValue = Number(temp);
    const idealTemp = 21.5;
    if (tempValue < idealTemp) {
      return Math.max(0.70, 1 - 0.01 * (idealTemp - tempValue));
    } else {
      return Math.max(0.70, 1 - 0.015 * (tempValue - idealTemp));
    }
  };

  // Componente para Cognição
  const CognitionView = () => {
    // Usar os refs e state compartilhados do componente pai
    // Isso garante que o estado persista mesmo quando StrategicData re-renderiza
    
    // Manter o estado do modal sincronizado com o ref compartilhado
    useEffect(() => {
      // Se o ref compartilhado tem um valor mas o state não, sincronizar
      if (modalStateRef.current && !modalState) {
        setModalState(modalStateRef.current);
      }
      // Se o state tem um valor mas o ref compartilhado não, sincronizar
      if (modalState && !modalStateRef.current) {
        modalStateRef.current = modalState;
      }
    }, [modalState]);
    
    // Manter o modal aberto mesmo durante atualizações de dados
    const handleOpenModal = (factorKey) => {
      modalKeyRef.current += 1;
      modalStateRef.current = factorKey;
      setModalState(factorKey);
    };
    
    const handleCloseModal = () => {
      modalStateRef.current = null;
      setModalState(null);
    };
    
    // Sempre usar o valor do ref compartilhado para garantir persistência
    const openReference = modalStateRef.current || modalState;

    // Calcular fatores localmente se não vierem do backend
    const fCO2 = strategic.cognition.factors?.fCO2 ?? calculateFCO2Local(current.co2);
    const fPM25 = strategic.cognition.factors?.fPM25 ?? calculateFPM25Local(current.pm25);
    const fTemp = strategic.cognition.factors?.fTemp ?? calculateFTempLocal(current.temperature);

    // Calcular índices localmente se não vierem do backend
    const concentrationIndex = useMemo(() => {
      if (strategic.cognition.concentrationIndex != null) {
        return strategic.cognition.concentrationIndex;
      }
      if (fCO2 !== null && fPM25 !== null && fTemp !== null) {
        const ic = 100 * fCO2 * fPM25 * fTemp;
        return Math.round(ic * 100) / 100;
      }
      return null;
    }, [strategic.cognition.concentrationIndex, fCO2, fPM25, fTemp]);
    
    const productivityIndex = useMemo(() => {
      if (strategic.cognition.productivityIndex != null) {
        return strategic.cognition.productivityIndex;
      }
      if (strategic.cognition.index != null) {
        return strategic.cognition.index;
      }
      if (fCO2 !== null && fPM25 !== null && fTemp !== null) {
        const ip = 100 * Math.pow(fCO2, 0.7) * Math.pow(fPM25, 0.8) * Math.pow(fTemp, 0.5);
        return Math.round(ip * 100) / 100;
      }
      return null;
    }, [strategic.cognition.productivityIndex, strategic.cognition.index, fCO2, fPM25, fTemp]);

    const cognitiveFactors = useMemo(() => [
      { 
        name: 'Concentração', 
        value: concentrationIndex,
        icon: FiTarget,
        description: 'Capacidade de foco e atenção',
        formula: 'IC = 100 × fCO₂ × fPM2.5 × fTemp',
        factorKey: 'concentration'
      },
      { 
        name: 'Produtividade', 
        value: productivityIndex,
        icon: FiTrendingUp,
        description: 'Eficiência no trabalho e propensão a erros',
        formula: 'IP = 100 × fCO₂^0.7 × fPM2.5^0.8 × fTemp^0.5',
        factorKey: 'productivity'
      }
    ], [concentrationIndex, productivityIndex]);

    const factorDetails = useMemo(() => [
      {
        name: 'CO₂',
        value: current.co2,
        unit: 'ppm',
        factor: fCO2 !== null ? Math.round(fCO2 * 1000) / 1000 : null,
        factorKey: 'co2'
      },
      {
        name: 'PM2.5',
        value: current.pm25,
        unit: 'µg/m³',
        factor: fPM25 !== null ? Math.round(fPM25 * 1000) / 1000 : null,
        factorKey: 'pm25'
      },
      {
        name: 'Temperatura',
        value: current.temperature,
        unit: '°C',
        factor: fTemp !== null ? Math.round(fTemp * 1000) / 1000 : null,
        factorKey: 'temp'
      }
    ], [current.co2, current.pm25, current.temperature, fCO2, fPM25, fTemp]);

    return (
      <div className="metric-view-container">
        {/* Header Premium */}
        <div className="metric-header-premium cognition-gradient">
          <div className="header-pattern"></div>
          <div className="header-content">
            <div className="header-left">
              <motion.div className="header-icon-box">
                <FiTarget size={36} />
              </motion.div>
              <div>
                <h1 className="header-title">Índice Cognitivo</h1>
                <p className="header-subtitle">Correlação entre condições ambientais e desempenho cognitivo</p>
              </div>
            </div>
            <div className="header-right">
              <div className="main-metric">
                <SafeCountUp 
                  value={productivityIndex}
                  suffix="%"
                  duration={2}
                  className="metric-value-huge"
                />
                <div className={`metric-status status-${
                  productivityIndex != null && productivityIndex >= 80 ? 'excellent' : 
                  productivityIndex != null && productivityIndex >= 60 ? 'good' : 
                  productivityIndex != null && productivityIndex >= 40 ? 'moderate' : 'poor'
                }`}>
                  {strategic.cognition.classification?.toUpperCase() || 
                   (productivityIndex != null 
                     ? (productivityIndex >= 80 ? 'EXCELENTE' : 
                        productivityIndex >= 60 ? 'BOM' : 
                        productivityIndex >= 40 ? 'MODERADO' : 'RUIM')
                     : 'SEM DADOS')}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Cognitive Factors Grid */}
        <div className="cognitive-grid">
          {cognitiveFactors.map((factor, index) => {
            const IconComponent = factor.icon;
            return (
              <motion.div 
                key={factor.name}
                className="cognitive-card"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -5 }}
              >
                <div className="cognitive-card-header">
                  <div className="cognitive-icon">
                    <IconComponent size={24} />
                  </div>
                  <button
                    className="info-icon-btn"
                    onClick={() => handleOpenModal(factor.factorKey)}
                    aria-label="Ver referências científicas"
                  >
                    <FiInfo size={18} />
                  </button>
                </div>
                <h4 className="cognitive-name">{factor.name}</h4>
                <p className="cognitive-description">{factor.description}</p>
                <div className="cognitive-meter">
                  <div className="meter-background">
                    <motion.div 
                      className="meter-fill"
                      initial={{ width: 0 }}
                      animate={{ width: `${factor.value != null ? factor.value : 0}%` }}
                      transition={{ duration: 1, delay: index * 0.2 }}
                      style={{
                        background: factor.value >= 80 ? '#10b981' : 
                                   factor.value >= 60 ? '#f59e0b' : '#ef4444'
                      }}
                    />
                  </div>
                  <span className="meter-value">
                    {factor.value != null ? `${factor.value.toFixed(1)}%` : 'N/A'}
                  </span>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Fatores Individuais */}
        <div className="cognition-factors-section">
          <h3 className="section-title">Fatores Ambientais</h3>
          <div className="factors-grid">
            {factorDetails.map((factor, index) => (
              <motion.div
                key={factor.name}
                className="factor-detail-card"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <div className="factor-detail-header">
                  <h4>{factor.name}</h4>
                  <button
                    className="info-icon-btn-small"
                    onClick={() => handleOpenModal(factor.factorKey)}
                    aria-label="Ver referências científicas"
                  >
                    <FiInfo size={16} />
                  </button>
                </div>
                <div className="factor-detail-content">
                  <div className="factor-value">
                    {factor.value != null && !isNaN(factor.value)
                      ? `${parseFloat(factor.value).toFixed(2)} ${factor.unit}`
                      : 'N/A'}
                  </div>
                  <div className="factor-multiplier">
                    <span className="factor-label">Fator (f{factor.name === 'PM2.5' ? 'PM2.5' : factor.name}):</span>
                    <span className="factor-number">
                      {factor.factor != null && !isNaN(factor.factor)
                        ? factor.factor.toFixed(3)
                        : 'N/A'}
                    </span>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Impacto */}
        {(strategic.cognition.impact || productivityIndex != null) && (
          <div className="cognition-impact">
            <div className="impact-card">
              <FiInfo className="impact-icon" />
              <div className="impact-content">
                <h4>Impacto no Desempenho</h4>
                <p>{strategic.cognition.impact || 
                  (productivityIndex != null 
                    ? (productivityIndex >= 80 ? "Condições ideais para alta produtividade" :
                       productivityIndex >= 60 ? "Condições adequadas para trabalho produtivo" :
                       productivityIndex >= 40 ? "Queda moderada na produtividade" :
                       "Queda significativa na produtividade e aumento de erros")
                    : "Dados insuficientes")}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Modal de Referências - Sempre renderizar se ref indicar que está aberto */}
        {(modalStateRef.current || modalState) && (
          <AnimatePresence mode="wait">
            <ScientificReferenceTooltip
              key={`modal-${modalStateRef.current || modalState}-${modalKeyRef.current}`}
              factor={modalStateRef.current || modalState}
              isOpen={true}
              onClose={handleCloseModal}
            />
          </AnimatePresence>
        )}
      </div>
    );
  };

  // Componente de Referências Científicas para Saúde
  const HealthScientificReferenceTooltip = ({ factor, isOpen, onClose }) => {
    const references = {
      respiratory: {
        title: "Índice Respiratório",
        description: "Avalia o impacto da qualidade do ar na saúde respiratória, considerando PM2.5, PM10 e CO₂.",
        formula: "IR = 100 × fPM2.5_resp × fPM10_resp × fCO2_resp",
        formulaDetails: "Onde cada fator varia entre 0 e 1, baseado em limites da OMS e EPA para saúde respiratória.",
        references: [
          {
            author: "WHO, 2021",
            title: "Air Quality Guidelines - Global Update",
            details: "PM2.5: média anual 5 µg/m³, média 24h 15 µg/m³. PM10: média anual 15 µg/m³, média 24h 45 µg/m³",
            page: "p. 12-15",
            pdf: "WHO_AQG_2021.pdf"
          },
          {
            author: "EPA, 2020",
            title: "Integrated Science Assessment for Particulate Matter",
            details: "PM2.5 associado a aumento de mortalidade por doenças respiratórias e cardiovasculares",
            page: "p. 3-8",
            pdf: "EPA_ISA_PM_2020.pdf"
          },
          {
            author: "Allen et al., 2016",
            title: "Cognitive Function & IAQ - Green vs Conventional office",
            details: "CO₂ elevado (>1000 ppm) associado a sintomas respiratórios e desconforto",
            page: "p. 5-7",
            pdf: "allen-et-al-2015-associations-o.pdf"
          }
        ]
      },
      thermal: {
        title: "Índice Térmico",
        description: "Avalia o impacto da temperatura e umidade na saúde térmica e bem-estar dos ocupantes.",
        formula: "IT = 100 × fTemp_health × fHumidity_health",
        formulaDetails: "Baseado em faixas de conforto térmico e limites de saúde estabelecidos pela ASHRAE e OMS.",
        references: [
          {
            author: "ASHRAE Standard 55-2020",
            title: "Thermal Environmental Conditions for Human Occupancy",
            details: "Faixa de conforto: 20-26°C, umidade relativa 30-60% para saúde e conforto",
            page: "p. 8-12",
            pdf: "ASHRAE_55_2020.pdf"
          },
          {
            author: "WHO, 2018",
            title: "Housing and Health Guidelines",
            details: "Temperaturas abaixo de 18°C aumentam risco de problemas respiratórios. Acima de 26°C aumenta risco cardiovascular",
            page: "p. 45-50",
            pdf: "WHO_Housing_Health_2018.pdf"
          },
          {
            author: "Seppänen & Fisk, LBNL-60946",
            title: "Temperature and Performance",
            details: "Umidade abaixo de 30% causa ressecamento das vias aéreas. Acima de 70% favorece crescimento de fungos",
            page: "p. 2-4",
            pdf: "lbnl-60946.pdf"
          }
        ]
      },
      visual: {
        title: "Índice Visual",
        description: "Avalia o impacto da iluminação na saúde visual e bem-estar, prevenindo fadiga ocular e distúrbios do sono.",
        formula: "IV = 100 × fLuminosity",
        formulaDetails: "Baseado em recomendações da IESNA e estudos sobre iluminação e saúde circadiana.",
        references: [
          {
            author: "IESNA, 2020",
            title: "Lighting Handbook - 10th Edition",
            details: "Iluminação adequada em escritórios: 300-500 lux para tarefas gerais, 500-750 lux para tarefas detalhadas",
            page: "p. 15-20",
            pdf: "IESNA_Handbook_2020.pdf"
          },
          {
            author: "Figueiro et al., 2017",
            title: "Light and Human Health",
            details: "Exposição à luz azul durante o dia melhora alerta e desempenho. Exposição noturna pode perturbar o sono",
            page: "p. 8-12",
            pdf: "Figueiro_Light_Health_2017.pdf"
          }
        ]
      },
      general: {
        title: "Índice Geral de Saúde",
        description: "Combinação ponderada dos índices respiratório, térmico e visual para avaliar o impacto geral do ambiente na saúde.",
        formula: "IG = 100 × (IR^0.4 × IT^0.3 × IV^0.3)",
        formulaDetails: "Peso maior para saúde respiratória (40%), seguido de térmico (30%) e visual (30%), refletindo impacto relativo na saúde geral.",
        references: [
          {
            author: "Fisk, 2017",
            title: "Review: Health and Productivity Benefits of Improved IEQ",
            details: "Qualidade do ar interior tem maior impacto na saúde geral, seguida de condições térmicas e iluminação",
            page: "p. 5-10",
            pdf: "Fisk_Health_Productivity_2017.pdf"
          },
          {
            author: "Mendell et al., 2011",
            title: "Association of Classroom Ventilation with Reduced Illness Absence",
            details: "Melhorias na qualidade do ar reduzem absenteísmo por doenças em 3-4%",
            page: "p. 2-5",
            pdf: "Mendell_Ventilation_2011.pdf"
          }
        ]
      }
    };

    const refData = references[factor];
    if (!refData || !isOpen) return null;

    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="reference-overlay"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="reference-modal"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="reference-modal-header">
            <h3>{refData.title}</h3>
            <button className="close-btn" onClick={onClose}>
              <FiXCircle size={24} />
            </button>
          </div>
          <div className="reference-modal-content">
            <p className="reference-description">{refData.description}</p>
            <div className="reference-formula">
              <strong>Fórmula:</strong>
              <code>{refData.formula}</code>
              {refData.formulaDetails && (
                <p className="formula-details">{refData.formulaDetails}</p>
              )}
            </div>
            <div className="reference-list">
              <h4>Referências Científicas:</h4>
              {refData.references.map((ref, idx) => (
                <div key={idx} className="reference-item">
                  <div className="reference-author">{ref.author}</div>
                  <div className="reference-title">{ref.title}</div>
                  <div className="reference-details">{ref.details}</div>
                  <div className="reference-citation">
                    <span className="reference-page">Página: {ref.page}</span>
                    <span className="reference-pdf">PDF: {ref.pdf}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </motion.div>
      </motion.div>
    );
  };

  // Funções para calcular fatores de saúde
  const calculateRespiratoryFactors = (current) => {
    // Fator PM2.5 para saúde respiratória (baseado em OMS 2021)
    const calculateFPM25Resp = (pm25) => {
      if (pm25 == null || isNaN(pm25)) return null;
      const pmValue = Number(pm25);
      if (pmValue <= 5) return 1.0; // Excelente (OMS média anual)
      if (pmValue <= 15) return 0.95 - ((pmValue - 5) / 10) * 0.15; // Bom (OMS média 24h)
      if (pmValue <= 25) return 0.80 - ((pmValue - 15) / 10) * 0.20; // Moderado
      if (pmValue <= 35) return 0.60 - ((pmValue - 25) / 10) * 0.20; // Ruim
      return Math.max(0.20, 0.40 - ((pmValue - 35) / 20) * 0.20); // Muito ruim
    };

    // Fator PM10 para saúde respiratória (baseado em OMS 2021)
    const calculateFPM10Resp = (pm10) => {
      if (pm10 == null || isNaN(pm10)) return null;
      const pmValue = Number(pm10);
      if (pmValue <= 15) return 1.0; // Excelente (OMS média anual)
      if (pmValue <= 45) return 0.95 - ((pmValue - 15) / 30) * 0.15; // Bom (OMS média 24h)
      if (pmValue <= 60) return 0.80 - ((pmValue - 45) / 15) * 0.20; // Moderado
      return Math.max(0.30, 0.60 - ((pmValue - 60) / 40) * 0.30); // Ruim
    };

    // Fator CO2 para saúde respiratória (baseado em Allen 2016)
    const calculateFCO2Resp = (co2) => {
      if (co2 == null || isNaN(co2)) return null;
      const co2Value = Number(co2);
      if (co2Value <= 600) return 1.0; // Excelente
      if (co2Value <= 1000) return 1.0 - ((co2Value - 600) / 400) * 0.10; // Bom
      if (co2Value <= 1500) return 0.90 - ((co2Value - 1000) / 500) * 0.20; // Moderado
      return Math.max(0.50, 0.70 - ((co2Value - 1500) / 1000) * 0.20); // Ruim
    };

    return {
      fPM25: calculateFPM25Resp(current?.pm25),
      fPM10: calculateFPM10Resp(current?.pm10),
      fCO2: calculateFCO2Resp(current?.co2)
    };
  };

  const calculateThermalFactors = (current) => {
    // Fator Temperatura para saúde (baseado em ASHRAE 55 e WHO)
    const calculateFTempHealth = (temp) => {
      if (temp == null || isNaN(temp)) return null;
      const tempValue = Number(temp);
      if (tempValue >= 20 && tempValue <= 26) return 1.0; // Ideal (ASHRAE)
      if (tempValue >= 18 && tempValue < 20) return 0.95 - ((20 - tempValue) / 2) * 0.15; // Levemente frio
      if (tempValue > 26 && tempValue <= 28) return 0.95 - ((tempValue - 26) / 2) * 0.15; // Levemente quente
      if (tempValue < 18) return Math.max(0.50, 0.80 - ((18 - tempValue) / 5) * 0.30); // Frio (risco respiratório)
      return Math.max(0.50, 0.80 - ((tempValue - 28) / 5) * 0.30); // Quente (risco cardiovascular)
    };

    // Fator Umidade para saúde (baseado em ASHRAE e WHO)
    const calculateFHumidityHealth = (humidity) => {
      if (humidity == null || isNaN(humidity)) return null;
      const humValue = Number(humidity);
      if (humValue >= 40 && humValue <= 60) return 1.0; // Ideal
      if (humValue >= 30 && humValue < 40) return 0.95 - ((40 - humValue) / 10) * 0.10; // Levemente seco
      if (humValue > 60 && humValue <= 70) return 0.95 - ((humValue - 60) / 10) * 0.10; // Levemente úmido
      if (humValue < 30) return Math.max(0.70, 0.90 - ((30 - humValue) / 10) * 0.20); // Seco (ressecamento)
      return Math.max(0.70, 0.90 - ((humValue - 70) / 10) * 0.20); // Úmido (fungos)
    };

    return {
      fTemp: calculateFTempHealth(current?.temperature),
      fHumidity: calculateFHumidityHealth(current?.humidity)
    };
  };

  const calculateVisualFactor = (current) => {
    // Fator Iluminação para saúde visual (baseado em IESNA)
    const calculateFLuminosity = (luminosity) => {
      if (luminosity == null || isNaN(luminosity)) return null;
      const luxValue = Number(luminosity);
      if (luxValue >= 300 && luxValue <= 750) return 1.0; // Ideal (IESNA)
      if (luxValue >= 200 && luxValue < 300) return 0.90 - ((300 - luxValue) / 100) * 0.10; // Levemente escuro
      if (luxValue > 750 && luxValue <= 1000) return 0.95 - ((luxValue - 750) / 250) * 0.05; // Levemente brilhante
      if (luxValue < 200) return Math.max(0.60, 0.85 - ((200 - luxValue) / 100) * 0.25); // Escuro (fadiga)
      return Math.max(0.80, 0.95 - ((luxValue - 1000) / 500) * 0.15); // Muito brilhante (desconforto)
    };

    return calculateFLuminosity(current?.luminosity);
  };

  // Componente para Saúde
  const HealthView = () => {
    // Usar os refs e state compartilhados do componente pai
    useEffect(() => {
      if (modalStateRef.current && !modalState) {
        setModalState(modalStateRef.current);
      }
      if (modalState && !modalStateRef.current) {
        modalStateRef.current = modalState;
      }
    }, [modalState]);

    const handleOpenModal = (factorKey) => {
      modalKeyRef.current += 1;
      modalStateRef.current = factorKey;
      setModalState(factorKey);
    };

    const handleCloseModal = () => {
      modalStateRef.current = null;
      setModalState(null);
    };

    const openReference = modalStateRef.current || modalState;

    // Calcular fatores de saúde
    const respiratoryFactors = calculateRespiratoryFactors(current);
    const thermalFactors = calculateThermalFactors(current);
    const visualFactor = calculateVisualFactor(current);

    // Calcular índices individuais
    const respiratoryIndex = useMemo(() => {
      const factors = [respiratoryFactors.fPM25, respiratoryFactors.fPM10, respiratoryFactors.fCO2].filter(f => f !== null);
      if (factors.length === 0) return null;
      // Não usar valores mockados - usar apenas fatores disponíveis
      const product = factors.reduce((acc, f) => acc * f, 1);
      const ir = 100 * product;
      return Math.round(ir * 100) / 100;
    }, [respiratoryFactors]);

    const thermalIndex = useMemo(() => {
      const factors = [thermalFactors.fTemp, thermalFactors.fHumidity].filter(f => f !== null);
      if (factors.length === 0) return null;
      // Não usar valores mockados - usar apenas fatores disponíveis
      const product = factors.reduce((acc, f) => acc * f, 1);
      const it = 100 * product;
      return Math.round(it * 100) / 100;
    }, [thermalFactors]);

    const visualIndex = useMemo(() => {
      if (visualFactor === null) return null;
      const iv = visualFactor * 100;
      return Math.round(iv * 100) / 100;
    }, [visualFactor]);

    const generalIndex = useMemo(() => {
      // Só calcular se tiver pelo menos um índice disponível
      const availableIndices = [respiratoryIndex, thermalIndex, visualIndex].filter(i => i !== null);
      if (availableIndices.length === 0) {
        // Se não tiver nenhum índice, retornar null (não mockar)
        return strategic.health?.index ?? null;
      }
      
      // Ajustar pesos baseado nos índices disponíveis
      let ir = respiratoryIndex;
      let it = thermalIndex;
      let iv = visualIndex;
      
      // Se algum índice estiver faltando, redistribuir os pesos
      if (ir === null && it !== null && iv !== null) {
        // Sem respiratório: IT 50%, IV 50%
        const ig = 100 * Math.pow(it / 100, 0.5) * Math.pow(iv / 100, 0.5);
        return Math.round(ig * 100) / 100;
      } else if (it === null && ir !== null && iv !== null) {
        // Sem térmico: IR 60%, IV 40%
        const ig = 100 * Math.pow(ir / 100, 0.6) * Math.pow(iv / 100, 0.4);
        return Math.round(ig * 100) / 100;
      } else if (iv === null && ir !== null && it !== null) {
        // Sem visual: IR 60%, IT 40%
        const ig = 100 * Math.pow(ir / 100, 0.6) * Math.pow(it / 100, 0.4);
        return Math.round(ig * 100) / 100;
      } else if (ir === null && it === null && iv !== null) {
        // Apenas visual
        return iv;
      } else if (ir === null && iv === null && it !== null) {
        // Apenas térmico
        return it;
      } else if (it === null && iv === null && ir !== null) {
        // Apenas respiratório
        return ir;
      }
      
      // Todos disponíveis: IG = 100 × (IR^0.4 × IT^0.3 × IV^0.3)
      const ig = 100 * Math.pow(ir / 100, 0.4) * Math.pow(it / 100, 0.3) * Math.pow(iv / 100, 0.3);
      return Math.round(ig * 100) / 100;
    }, [respiratoryIndex, thermalIndex, visualIndex, strategic.health?.index]);

    // Filtrar apenas métricas que têm dados reais disponíveis
    const healthMetrics = useMemo(() => {
      const metrics = [];
      
      // Respiratório - só incluir se tiver dados de PM2.5, PM10 ou CO2
      const respValue = strategic.health?.respiratoryIndex ?? respiratoryIndex;
      if (respValue != null) {
        metrics.push({
          name: 'Respiratório',
          value: respValue,
          icon: FiWind,
          color: '#10b981',
          factorKey: 'respiratory',
          description: 'Impacto da qualidade do ar na saúde respiratória',
          hasData: true
        });
      }
      
      // Térmico - só incluir se tiver dados de temperatura ou umidade
      const thermalValue = strategic.health?.thermalIndex ?? thermalIndex;
      if (thermalValue != null) {
        metrics.push({
          name: 'Térmico',
          value: thermalValue,
          icon: FiThermometer,
          color: '#3b82f6',
          factorKey: 'thermal',
          description: 'Impacto da temperatura e umidade na saúde térmica',
          hasData: true
        });
      }
      
      // Visual - só incluir se tiver dados de luminosidade
      const visualValue = strategic.health?.visualIndex ?? visualIndex;
      if (visualValue != null && current.luminosity != null) {
        metrics.push({
          name: 'Visual',
          value: visualValue,
          icon: FiSun,
          color: '#f59e0b',
          factorKey: 'visual',
          description: 'Impacto da iluminação na saúde visual',
          hasData: true
        });
      } else if (current.luminosity == null) {
        // Sinalizar que luminosidade não está disponível
        metrics.push({
          name: 'Visual',
          value: null,
          icon: FiSun,
          color: '#9ca3af',
          factorKey: 'visual',
          description: 'Dados de iluminação não disponíveis neste sensor',
          hasData: false
        });
      }
      
      // Geral - só incluir se tiver pelo menos um índice calculado
      const generalValue = strategic.health?.generalIndex ?? generalIndex;
      if (generalValue != null) {
        metrics.push({
          name: 'Geral',
          value: generalValue,
          icon: FiHeart,
          color: '#ef4444',
          factorKey: 'general',
          description: 'Índice geral combinado de saúde ambiental',
          hasData: true
        });
      }
      
      return metrics;
    }, [respiratoryIndex, thermalIndex, visualIndex, generalIndex, strategic.health, current.luminosity]);

    return (
      <div className="metric-view-container">
        {/* Header Premium */}
        <div className="metric-header-premium health-gradient">
          <div className="header-pattern"></div>
          <div className="header-content">
            <div className="header-left">
              <motion.div 
                className="header-icon-box"
                animate={{ scale: [1, 1.1, 1] }}
                transition={{ duration: 2, repeat: Infinity }}
              >
                <FiHeart size={36} />
              </motion.div>
              <div>
                <h1 className="header-title">Índice de Saúde</h1>
                <p className="header-subtitle">Análise combinada do impacto ambiental na saúde dos ocupantes</p>
              </div>
            </div>
            <div className="header-right">
              <div className="main-metric">
                {generalIndex != null || strategic.health?.index != null ? (
                  <>
                    <SafeCountUp 
                      value={generalIndex ?? strategic.health?.index}
                      suffix="/100"
                      duration={2}
                      className="metric-value-huge"
                    />
                    <div className={`metric-status status-${
                      (generalIndex ?? strategic.health?.index) >= 80 ? 'excellent' : 
                      (generalIndex ?? strategic.health?.index) >= 60 ? 'good' : 'moderate'
                    }`}>
                      {strategic.health?.factors?.length === 0 ? 'CONDIÇÕES IDEAIS' : 'ATENÇÃO NECESSÁRIA'}
                    </div>
                  </>
                ) : (
                  <>
                    <div className="metric-value-huge">N/A</div>
                    <div className="metric-status status-poor">
                      DADOS INSUFICIENTES
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Health Metrics */}
        <div className="health-metrics-grid">
          {healthMetrics.map((metric, index) => {
            const IconComponent = metric.icon;
            return (
              <motion.div 
                key={metric.name}
                className="health-metric-card"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ scale: 1.05 }}
              >
                <div className="health-metric-card-header">
                  <div className="metric-circle" style={{ borderColor: metric.hasData === false ? '#9ca3af' : metric.color, opacity: metric.hasData === false ? 0.5 : 1 }}>
                    {metric.hasData !== false && metric.value != null ? (
                      <svg className="progress-ring" width="120" height="120">
                        <circle
                          className="progress-ring-bg"
                          strokeWidth="8"
                          stroke="#e5e7eb"
                          fill="transparent"
                          r="52"
                          cx="60"
                          cy="60"
                        />
                        <motion.circle
                          className="progress-ring-fill"
                          strokeWidth="8"
                          stroke={metric.color}
                          fill="transparent"
                          r="52"
                          cx="60"
                          cy="60"
                          strokeDasharray={`${2 * Math.PI * 52}`}
                          strokeDashoffset={`${2 * Math.PI * 52 * (1 - metric.value / 100)}`}
                          initial={{ strokeDashoffset: `${2 * Math.PI * 52}` }}
                          animate={{ strokeDashoffset: `${2 * Math.PI * 52 * (1 - metric.value / 100)}` }}
                          transition={{ duration: 1, delay: index * 0.2 }}
                        />
                      </svg>
                    ) : (
                      <svg className="progress-ring" width="120" height="120">
                        <circle
                          className="progress-ring-bg"
                          strokeWidth="8"
                          stroke="#e5e7eb"
                          fill="transparent"
                          r="52"
                          cx="60"
                          cy="60"
                        />
                      </svg>
                    )}
                    <div className="metric-center">
                      <IconComponent size={24} color={metric.hasData === false ? '#9ca3af' : metric.color} />
                      <span className="metric-percent">
                        {metric.hasData === false ? 'N/D' : 
                         metric.value != null ? `${metric.value.toFixed(0)}%` : 'N/A'}
                      </span>
                    </div>
                  </div>
                  {metric.hasData !== false && (
                    <button
                      className="info-icon-btn-health"
                      onClick={() => handleOpenModal(metric.factorKey)}
                      aria-label="Ver referências científicas"
                    >
                      <FiInfo size={18} />
                    </button>
                  )}
                </div>
                <h4 className="metric-name">{metric.name}</h4>
                <p className="metric-description">{metric.description}</p>
                {metric.hasData === false && (
                  <div className="metric-no-data-badge">
                    <FiAlertCircle size={14} />
                    <span>Sensor não possui esta leitura</span>
                  </div>
                )}
              </motion.div>
            );
          })}
        </div>

        {/* Health Factors */}
        {strategic.health.factors.length > 0 && (
          <div className="health-factors">
            <h3 className="section-title">Fatores de Atenção</h3>
            <div className="factors-list">
              {strategic.health.factors.map((factor, index) => (
                <motion.div 
                  key={index}
                  className="factor-item"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <FiAlertCircle className="factor-icon" />
                  <span>{typeof factor === 'object' ? factor.name : factor}</span>
                </motion.div>
              ))}
            </div>
          </div>
        )}

        {/* Modal de Referências Científicas */}
        {(modalStateRef.current || modalState) && (
          <AnimatePresence mode="wait">
            <HealthScientificReferenceTooltip
              key={`health-modal-${modalStateRef.current || modalState}-${modalKeyRef.current}`}
              factor={modalStateRef.current || modalState}
              isOpen={true}
              onClose={handleCloseModal}
            />
          </AnimatePresence>
        )}
      </div>
    );
  };

  // Componente para Eficiência HVAC
  const EfficiencyView = () => {
    const efficiencyData = useMemo(() => {
      // Calcular variação de umidade absoluta ao longo do tempo
      return historical.slice(-12).map((item, index, array) => {
        const currentHumidity = item.humidity || 45;
        const previousHumidity = index > 0 ? (array[index - 1].humidity || 45) : currentHumidity;
        
        // Delta de umidade absoluta (adimensional) - variação percentual
        const deltaHumidity = Math.abs(currentHumidity - previousHumidity) / 100;
        
        // Calcular perda de eficiência baseada na variação
        const efficiencyLoss = deltaHumidity * 10; // Multiplicador para escala
        
        return {
          time: new Date(item.timestamp).toLocaleTimeString('pt-BR', { hour: '2-digit' }),
          deltaHumidity: parseFloat(deltaHumidity.toFixed(3)),
          efficiencyLoss: parseFloat(efficiencyLoss.toFixed(2)),
          economia: parseFloat((15 + Math.sin(index * 0.5) * 5).toFixed(1))
        };
      });
    }, [historical]);

    return (
      <div className="metric-view-container">
        {/* Header Premium */}
        <div className="metric-header-premium efficiency-gradient">
          <div className="header-pattern"></div>
          <div className="header-content">
            <div className="header-left">
              <motion.div className="header-icon-box">
                <FiZap size={36} />
              </motion.div>
              <div>
                <h1 className="header-title">Eficiência HVAC</h1>
                <p className="header-subtitle">Variação de umidade absoluta e perda de eficiência</p>
              </div>
            </div>
            <div className="header-right">
              <div className="main-metric">
                <SafeCountUp 
                  value={strategic.efficiency.deltaHumidity}
                  decimals={3}
                  duration={2}
                  className="metric-value-huge"
                />
                <div className={`metric-status status-${
                  (strategic.efficiency.deltaHumidity || 0) < 0.5 ? 'excellent' : 
                  (strategic.efficiency.deltaHumidity || 0) < 0.8 ? 'good' : 'moderate'
                }`}>
                  {strategic.efficiency.status.toUpperCase()}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Efficiency Metrics */}
        <div className="efficiency-metrics">
          <motion.div className="efficiency-card" whileHover={{ scale: 1.02 }}>
            <div className="card-icon">
              <FiTrendingUp />
            </div>
            <div className="card-content">
              <h4>Economia Estimada</h4>
              <p className="card-value">{strategic.efficiency.estimatedSavings}</p>
              <span className="card-subtitle">Comparado ao baseline</span>
            </div>
          </motion.div>

          <motion.div className="efficiency-card" whileHover={{ scale: 1.02 }}>
            <div className="card-icon">
              <FiClock />
            </div>
            <div className="card-content">
              <h4>Tempo de Operação</h4>
              <p className="card-value">14h/dia</p>
              <span className="card-subtitle">Média últimas 24h</span>
            </div>
          </motion.div>

          <motion.div className="efficiency-card" whileHover={{ scale: 1.02 }}>
            <div className="card-icon">
              <FiActivity />
            </div>
            <div className="card-content">
              <h4>Ciclos ON/OFF</h4>
              <p className="card-value">8 ciclos</p>
              <span className="card-subtitle">Últimas 24h</span>
            </div>
          </motion.div>
        </div>

        {/* Delta Humidity Chart */}
        <div className="chart-container-advanced">
          <h3 className="chart-title">Variação de Umidade Absoluta (Delta)</h3>
          <ResponsiveContainer width="100%" height={350}>
            <ComposedChart data={efficiencyData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="time" stroke="#6b7280" />
              <YAxis yAxisId="left" stroke="#6b7280" />
              <YAxis yAxisId="right" orientation="right" stroke="#6b7280" />
              <Tooltip 
                shared={true}
                contentStyle={{
                  background: 'rgba(255, 255, 255, 0.95)',
                  border: 'none',
                  borderRadius: '12px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                }}
                formatter={(value, name) => {
                  if (value === null || value === undefined || isNaN(value)) {
                    return null;
                  }
                  return [parseFloat(value).toFixed(3), name];
                }}
              />
              <Legend />
              <Line 
                yAxisId="right" 
                type="monotone" 
                dataKey="deltaHumidity" 
                stroke="#3b82f6" 
                strokeWidth={3}
                dot={{ fill: '#3b82f6', r: 4 }}
                name="Delta Umidade"
                connectNulls={true}
                activeDot={{ r: 7, stroke: '#3b82f6', strokeWidth: 2, fill: '#fff' }}
              />
              <Line 
                yAxisId="left" 
                type="monotone" 
                dataKey="efficiencyLoss" 
                stroke="#ef4444" 
                strokeWidth={2}
                strokeDasharray="5 5"
                dot={false}
                name="Perda de Eficiência"
                connectNulls={true}
                activeDot={{ r: 6, stroke: '#ef4444', strokeWidth: 2, fill: '#fff' }}
              />
              <Line 
                yAxisId="left" 
                type="monotone" 
                dataKey="economia" 
                stroke="#10b981" 
                strokeWidth={2}
                dot={false}
                name="Economia (%)"
                connectNulls={true}
                activeDot={{ r: 6, stroke: '#10b981', strokeWidth: 2, fill: '#fff' }}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>
    );
  };

  // Componente para Renovação de Ar
  const VentilationView = () => {
    const ventilationData = useMemo(() => {
      // Analisar acúmulo de CO2 ao longo do tempo
      return historical.slice(-24).map((item, index, array) => {
        const currentCO2 = item.co2 || 420;
        const previousCO2 = index > 0 ? (array[index - 1].co2 || 420) : currentCO2;
        
        // Calcular acúmulo de CO2 (variação positiva indica acúmulo)
        const co2Accumulation = currentCO2 - previousCO2;
        
        // Estimar qualidade de renovação baseada no acúmulo
        // Acúmulo baixo (< 10 ppm) = boa renovação
        // Acúmulo médio (10-30 ppm) = renovação moderada
        // Acúmulo alto (> 30 ppm) = renovação insuficiente
        const renovationQuality = co2Accumulation < 10 ? 90 : 
                                 co2Accumulation < 30 ? 70 : 50;
        
        return {
          time: new Date(item.timestamp).toLocaleTimeString('pt-BR', { hour: '2-digit' }),
          co2: parseFloat(currentCO2.toFixed(0)),
          co2Accumulation: parseFloat(co2Accumulation.toFixed(1)),
          renovationQuality: parseFloat(renovationQuality.toFixed(0))
        };
      });
    }, [historical]);

    // Determinar status baseado no acúmulo de CO2
    const co2Status = strategic.ventilation.co2Accumulation || "Baixo";
    const statusClass = co2Status === "Baixo" ? 'excellent' : 
                       co2Status === "Moderado" ? 'good' : 'moderate';

    return (
      <div className="metric-view-container">
        {/* Header Premium */}
        <div className="metric-header-premium ventilation-gradient">
          <div className="header-pattern"></div>
          <div className="header-content">
            <div className="header-left">
              <motion.div 
                className="header-icon-box"
                animate={{ rotate: [0, 360] }}
                transition={{ duration: 4, repeat: Infinity, ease: "linear" }}
              >
                <FiRefreshCw size={36} />
              </motion.div>
              <div>
                <h1 className="header-title">Renovação de Ar</h1>
                <p className="header-subtitle">Análise de acúmulo de CO₂ para estimar renovação</p>
              </div>
            </div>
            <div className="header-right">
              <div className="main-metric">
                <div className="metric-value-huge" style={{ fontSize: '2.5rem' }}>
                  {co2Status}
                </div>
                <div className={`metric-status status-${statusClass}`}>
                  {strategic.ventilation.recommendation.toUpperCase()}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Ventilation Info Cards */}
        <div className="ventilation-info">
          <motion.div className="info-card" whileHover={{ y: -5 }}>
            <div className="info-header">
              <FiCloud className="info-icon" />
              <h4>Acúmulo de CO₂</h4>
            </div>
            <p className="info-value">{co2Status}</p>
            <p className="info-description">Baseado na variação de CO₂</p>
            <div className="info-indicator">
              <span className="indicator-label">Status:</span>
              <div className="indicator-bars">
                {[1,2,3].map(i => (
                  <div 
                    key={i}
                    className={`bar ${i <= (co2Status === "Baixo" ? 3 : co2Status === "Moderado" ? 2 : 1) ? 'active' : ''}`}
                  />
                ))}
              </div>
            </div>
          </motion.div>

          <motion.div className="info-card" whileHover={{ y: -5 }}>
            <div className="info-header">
              <FiWind className="info-icon" />
              <h4>CO₂ Atual</h4>
            </div>
            <p className="info-value">{current.co2 != null ? parseFloat(current.co2).toFixed(0) : 'N/A'} ppm</p>
            <p className="info-description">Nível atual de dióxido de carbono</p>
            <div className="co2-scale">
              <div className="scale-bar">
                <div 
                  className="scale-fill"
                  style={{ 
                    width: `${Math.min((current.co2 / 1500) * 100, 100)}%`,
                    background: current.co2 < 600 ? '#10b981' : 
                               current.co2 < 1000 ? '#f59e0b' : '#ef4444'
                  }}
                />
              </div>
              <div className="scale-labels">
                <span>0</span>
                <span>600</span>
                <span>1000</span>
                <span>1500</span>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Ventilation Chart */}
        <div className="chart-container-advanced">
          <h3 className="chart-title">Análise de Acúmulo de CO₂</h3>
          <ResponsiveContainer width="100%" height={350}>
            <ComposedChart data={ventilationData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="time" stroke="#6b7280" />
              <YAxis yAxisId="left" stroke="#6b7280" />
              <YAxis yAxisId="right" orientation="right" stroke="#6b7280" />
              <Tooltip 
                shared={true}
                contentStyle={{
                  background: 'rgba(255, 255, 255, 0.95)',
                  border: 'none',
                  borderRadius: '12px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                }}
                formatter={(value, name) => {
                  if (value === null || value === undefined || isNaN(value)) {
                    return null;
                  }
                  return [parseFloat(value).toFixed(1), name];
                }}
              />
              <Legend />
              <Area
                yAxisId="left"
                type="monotone"
                dataKey="co2Accumulation"
                stroke="#ef4444"
                fill="#ef4444"
                fillOpacity={0.3}
                name="Acúmulo CO₂ (ppm)"
                connectNulls={true}
                activeDot={{ r: 6, stroke: '#ef4444', strokeWidth: 2, fill: '#fff' }}
              />
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="co2"
                stroke="#3b82f6"
                strokeWidth={2}
                dot={false}
                name="CO₂ (ppm)"
                connectNulls={true}
                activeDot={{ r: 6, stroke: '#3b82f6', strokeWidth: 2, fill: '#fff' }}
              />
              <Line
                yAxisId="left"
                type="monotone"
                dataKey="renovationQuality"
                stroke="#10b981"
                strokeWidth={2}
                strokeDasharray="5 5"
                dot={false}
                name="Qualidade Renovação (%)"
                connectNulls={true}
                activeDot={{ r: 6, stroke: '#10b981', strokeWidth: 2, fill: '#fff' }}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>

        {/* Recommendations */}
        <div className="recommendations-section">
          <h3 className="section-title">Recomendações</h3>
          <div className="recommendation-cards">
            <div className="recommendation-card">
              <FiCheckCircle className="rec-icon success" />
              <p>Taxa de renovação dentro dos padrões ASHRAE 62.1</p>
            </div>
            <div className="recommendation-card">
              <FiInfo className="rec-icon info" />
              <p>Considerar aumento de ventilação em horários de pico</p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Renderização baseada na subseção ativa
  const renderContent = () => {
    switch (activeSubsection) {
      case 'comfort':
        return <ComfortView />;
      case 'airQuality':
        return <AirQualityView />;
      case 'cognition':
        return <CognitionView />;
      case 'health':
        return <HealthView />;
      case 'efficiency':
        return <EfficiencyView />;
      case 'ventilation':
        return <VentilationView />;
      default:
        return <ComfortView />;
    }
  };

  return renderContent();
};

export default memo(StrategicData);
